import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchStatisticsData } from "../../features/Statistics/Statistics";
import Card from "./Card";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Loading from "../Loading";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import getDirctionLang from "../../Actions/getDirctionLang";

function Statistics({ onVisible }) {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { statistics, loading } = useSelector((state) => state.statistics);
  const isRtl = getDirctionLang();

  useEffect(() => {
    dispatch(fetchStatisticsData(200));
  }, [dispatch, i18n.language]);

  if (loading) return <Loading />;

  return (
    <motion.section
      className="relative py-20 md:py-28 bg-slate-50 dark:bg-zinc-950 overflow-hidden transition-colors duration-500"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      onViewportEnter={() => {
        if (typeof onVisible === "function") onVisible();
      }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-16">
          <div className="flex flex-col items-start gap-4">
            
            {/* Tech Badge */}
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white dark:bg-slate-900/80 border border-emerald-500/20 dark:border-emerald-500/20 shadow-sm dark:shadow-[0_0_15px_rgba(16,185,129,0.05)] backdrop-blur-md">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.6)]"></div>
              <span className="text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm font-bold tracking-wide">
                {t("latestUpdate") || "کۆتا نوێکردنەوە"}: {statistics[0]?.last_update?.split("T")[0] || "2026-05-04"}
              </span>
            </div>
            
            {/* Solid Main Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-1 text-slate-800 dark:text-white transition-colors duration-500"
            >
              {t("zadStatistics") || "ئاماری زاد"}
            </motion.h2>
          </div>

          {/* Action Button (Desktop) - Minimal & Soft */}
          <Link to="/show/statistics" className="group hidden md:flex items-center justify-center gap-2 bg-transparent hover:bg-emerald-50 dark:hover:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors duration-300">
            <span>{t("seeMore") || "بینینی زیاتر"}</span>
            {isRtl ? (
              <FiArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
            ) : (
              <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            )}
          </Link>
        </div>

        {/* Clean Responsive Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-6">
          {statistics?.map((statistic, index) => (
            <motion.div 
              key={statistic.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6, ease: "easeOut" }}
              viewport={{ once: true, margin: "-50px" }}
              className="h-full"
            >
              <Card statistic={statistic} />
            </motion.div>
          ))}
        </div>

        {/* Action Button (Mobile) - Minimal & Soft */}
        <div className="mt-12 flex justify-center md:hidden">
          <Link to="/show/statistics" className="group flex items-center justify-center gap-2 w-full sm:w-auto bg-transparent hover:bg-emerald-50 dark:hover:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 rounded-lg px-5 py-2.5 text-sm font-semibold transition-colors duration-300">
            <span>{t("seeMore") || "بینینی زیاتر"}</span>
            {isRtl ? (
              <FiArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300" />
            ) : (
              <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            )}
          </Link>
        </div>

      </div>
    </motion.section>
  );
}

export default Statistics;
